import React, { useState, useEffect, Fragment } from 'react';
import { Breadcrumbs, H5 } from '../../../../AbstractElements';
import { Card, CardBody, CardHeader, Col, Container, Row, Input, FormGroup, Label } from 'reactstrap';
import DataTable from '../../../Common/DataTable';
import { PlusCircle, Edit } from 'react-feather';
import { fetchTableData } from '../../../../Api/ApiMethods';
import { useNavigate } from 'react-router-dom';
import { CustomerNumberFilter, CustomerNameFilter } from '../../../Common/Filters/CommonFilters';
import { actionButtonColumn } from '../../../Common/TableColumns';
import useDebouncedValue from '../../../../_helper/useDebouncedValue';
import { appendQueryParamIfMinLength, normalizeSearchFields } from '../../../../_helper/regexHandler';

const Customers = () => {
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [customerTypeFilter, setCustomerTypeFilter] = useState('Bireysel');
    const [customerNumberSearch, setCustomerNumberSearch] = useState('');
    const [customerNameSearch, setCustomerNameSearch] = useState('');

    const debouncedCustomerNumberSearch = useDebouncedValue(customerNumberSearch, 500);
    const debouncedCustomerNameSearch = useDebouncedValue(customerNameSearch, 500);

    const customerApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/customer';
    const navigate = useNavigate();

    useEffect(() => {
        let isMounted = true;
        const fetchCustomers = async () => {
            const { normalized, hasInvalid } = normalizeSearchFields([
                { key: 'customerNumber', value: debouncedCustomerNumberSearch, minLength: 3 },
                { key: 'customerName', value: debouncedCustomerNameSearch, minLength: 2 }
            ]);

            if (hasInvalid) {
                if (isMounted) setIsLoading(false);
                return;
            }

            setIsLoading(true);
            const queryParams = new URLSearchParams();
            if (customerTypeFilter) {
                queryParams.append('customerType', customerTypeFilter);
            }

            appendQueryParamIfMinLength(queryParams, 'taxOrIdentityNumber', normalized.customerNumber, 3);
            appendQueryParamIfMinLength(queryParams, 'customerNameOrCompanyName', normalized.customerName, 2);

            const apiUrl = queryParams.toString()
                ? `${customerApiUrl}?${queryParams.toString()}`
                : customerApiUrl;

            await fetchTableData({
                apiEndpoint: apiUrl,
                page,
                pageSize,
                search: null,
                setDataCallback: (fetchedData) => {
                    setData(fetchedData);
                },
                setTotalRowsCallback: setTotalRows,
            });

            if (isMounted) setIsLoading(false);
        };

        fetchCustomers();

        return () => {
            isMounted = false;
        };
    }, [page, pageSize, customerTypeFilter, debouncedCustomerNumberSearch, debouncedCustomerNameSearch]);

    const handleDetail = (id) => {
        navigate(`/customer/${id}`);
    };

    const handleNew = () => {
        navigate('/customer/new', { state: { isNew: true } });
    };

    // Handle customer type filter change
    const handleCustomerTypeChange = (e) => {
        // Reset pagination first, then update filter
        // This ensures we don't try to fetch non-existent pages
        setPage(1);
        setPageSize(10);
        setData([]); // Clear existing data to prevent showing incorrect data during loading
        setTotalRows(0); // Reset total rows count
        setCustomerTypeFilter(e.target.value);
        setCustomerNumberSearch('');
        setCustomerNameSearch('');
    };

    const handleCustomerNumberSearchChange = (e) => {
        setCustomerNumberSearch(e.target.value);
        setPage(1);
    };

    const handleCustomerNameSearchChange = (e) => {
        setCustomerNameSearch(e.target.value);
        setPage(1);
    };

    const editColumn = actionButtonColumn({
        name: 'Düzenle',
        icon: Edit,
        onClick: (row) => handleDetail(row.id)
    });

    const columns = customerTypeFilter === 'Bireysel' ? [
        { name: 'Müşteri Tipi', selector: row => row.customerType || '', sortable: true },
        { name: 'Vekaleti Alan', selector: row => row.agent ? `${row.agent.name || ''} ${row.agent.surname || ''}` : '', sortable: true },
        { name: 'Müşteri Adı', selector: row => row.clientName || '', sortable: true },
        { name: 'Kimlik Numarası', selector: row => row.identityNumber || '', sortable: true },
        { name: 'E-posta', selector: row => (row.emails && row.emails[0]?.email) || '', sortable: true },
        { name: 'Telefon', selector: row => (row.phones && row.phones[0]?.phone) || '', sortable: true },
        editColumn,
    ] : [
        { name: 'Müşteri Tipi', selector: row => row.customerType || '', sortable: true },
        { name: 'Vekaleti Alan', selector: row => row.agent ? `${row.agent.name || ''} ${row.agent.surname || ''}` : '', sortable: true },
        { name: 'Şirket Adı', selector: row => row.companyName || '', sortable: true },
        { name: 'Vergi Numarası', selector: row => row.taxNumber || '', sortable: true },
        { name: 'CRS Numarası', selector: row => row.crsNumber || '', sortable: true },
        { name: 'Şirket Temsilcisi', selector: row => row.companyRepresentative || '', sortable: true },
        { name: 'E-posta', selector: row => (row.emails && row.emails[0]?.email) || '', sortable: true },
        { name: 'Telefon', selector: row => (row.phones && row.phones[0]?.phone) || '', sortable: true },
        editColumn,
    ];

    return (
        <>
            <Breadcrumbs parent='Müşteri Alanları' title='Müşteriler' />
            <Container fluid={true}>
                <Row>
                    <Col sm='12'>
                        <Card>
                            <CardHeader>
                                <H5>Müşteriler</H5>
                                <span>Mevcut olan müşteriler aşağıda listelenmektedir.</span>
                            </CardHeader>
                            <CardBody>
                                <Row className="g-3 mb-3 align-items-center">
                                    <Col md="3" sm="6">
                                        <FormGroup>
                                            <Label>Müşteri Tipi</Label>
                                            <Input
                                                type="select"
                                                id="customerType"
                                                name="customerType"
                                                value={customerTypeFilter}
                                                onChange={handleCustomerTypeChange}
                                            >
                                                <option value="Bireysel">Bireysel</option>
                                                <option value="Kurumsal">Kurumsal</option>
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                    <CustomerNumberFilter
                                        value={customerNumberSearch}
                                        onChange={handleCustomerNumberSearchChange}
                                        label="Vergi / Kimlik No"
                                        placeholder="Vergi veya kimlik no ile ara"
                                    />
                                    <CustomerNameFilter
                                        value={customerNameSearch}
                                        onChange={handleCustomerNameSearchChange}
                                    />
                                    <Col md="3" sm="6" className="d-flex justify-content-end align-items-center">
                                        <div className="btn btn-outline-primary ms-auto" onClick={handleNew} style={{ height: '38px', display: 'inline-flex', alignItems: 'center' }}>
                                            <PlusCircle size={18} className="me-1" />
                                            {' Müşteri Ekle'}
                                        </div>
                                    </Col>
                                </Row>

                                <Fragment>
                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        pagination
                                        paginationServer
                                        stickyActionPosition="left"
                                        selectableRows={false}
                                        paginationTotalRows={totalRows}
                                        paginationPerPage={pageSize}
                                        paginationDefaultPage={page}
                                        onChangePage={page => setPage(page)}
                                        onChangeRowsPerPage={pageSize => setPageSize(pageSize)}
                                        progressPending={isLoading}
                                        progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                                        noDataComponent={isLoading ? null : "Müşteri bulunamadı"}
                                    />
                                </Fragment>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default Customers;
